<template>
  <v-row no-gutters class="fill-height flex-column">
    <v-col v-if="mapPanelDataFetched && isFetched">
      <profiles-widget
        v-if="withProfiles"
        v-bind="profilesData"
        :is-add-profile-disabled="allDataValid"
        :current-data="params"
        @changeCurrentProfile="changeCurrentProfile"
      ></profiles-widget>
      <stepper
        :ref="`waterBalanceStepper-${toolName}`"
        editable
        :steps="steps"
        :stepper-props="{
          altLabels: true,
        }"
        :stepper-classes="['elevation-0']"
        :stepper-header-classes="['elevation-0', 'pl-5']"
        :stepper-buttons-classes="['pl-8']"
      >
        <template v-for="step in steps.slice(0, -1)" #[step.step]="">
          <sidebar-row :key="step.step" style="padding-right: 0 !important">
            <v-form :ref="`step${step.step}`" v-model="stepsValidations[`step${step.step}`]">
              <sidebar-form
                v-model="params"
                :inputs="inputs.filter(input => input.step === step.step)"
                :hasIconColumn="false"
                class="wizard-form"
              >
                <template v-for="input in inputs.filter(input => input.step === step.step)" #[input.name]="data">
                  <data-input
                    v-bind="{
                      ...data,
                      dense: true,
                      ...(data.connectedAttribute
                        ? {
                            items: itemsList[data.connectedAttribute],
                            loading: inputLoading[data.connectedAttribute],
                            disabled: data.disabled || inputLoading[data.connectedAttribute],
                          }
                        : data.connectedLayer
                        ? { items: itemsList[data.connectedLayer] }
                        : {}),
                    }"
                    :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                    v-model="params[data.name]"
                    @change="inputChangeHandler(data, step.step)"
                    :key="input.name"
                  />
                </template>
              </sidebar-form>
            </v-form>
            <slot :name="`stepPrepend-${step.step}`"></slot>
          </sidebar-row>
        </template>
        <template #[steps.at(-1).step]="">
          <div class="d-flex pt-2 flex-column">
            <sidebar-row v-if="isAnalysisDone">
              <slot name="analysisDoneCallback">
                <v-col cols="auto" class="pa-0 pb-3">
                  <v-alert dense text type="success" class="ma-0 pa-0 text-left">
                    <template #prepend>
                      <v-icon aria-hidden="false" class="mr-2" color="success">mdi-check</v-icon>
                    </template>
                    <span class="text-left text-body-2">{{
                      $i18n.t('sidebar.layerAddedToProject', { name: layerName })
                    }}</span>
                  </v-alert>
                </v-col>
              </slot>
            </sidebar-row>
            <v-row class="pb-2 align-baseline">
              <v-col cols="1"></v-col>
              <v-col cols="auto" v-if="isAnalysisDone">
                <base-button-subheader
                  translation-path="sidebar.locks.reset"
                  @click="isResetDialogVisible = true"
                  :disabled="isAnalysisLoading"
                  cancel
                />
              </v-col>
              <v-col cols="5" v-if="!isAnalysisDone && useAnalysisDate">
                <data-input
                  v-model="params['analysis_date']"
                  :data-type="{ name: 'date' }"
                  locale-date-format="YYYY-MM-DD"
                  :label="$i18n.t('sidebar.analysisDate')"
                  return-type="date"
                  v-bind="{
                    rules: [v => !!v || $i18n.t('rules.required')],
                  }"
                />
              </v-col>
              <v-col cols="auto">
                <base-button-subheader
                  :translation-path="
                    isAnalysisDone ? 'sidebar.locks.turnOnAnalysisAgain' : 'sidebar.locks.turnOnAnalysis'
                  "
                  @click="turnOnAnalysis"
                  :disabled="!allDataValid"
                  :loading="isAnalysisLoading"
                />
              </v-col>
              <v-col cols="auto"></v-col>
            </v-row>
          </div>
        </template>
      </stepper>
      <main-dialog
        :is-visible.sync="isResetDialogVisible"
        :title="$i18n.t('dialog.title.resetWaterBalanceConfig')"
        :body="$i18n.t('dialog.body.resetWaterBalanceConfigConfirmation')"
        @agree="resetAnalysis"
        agree-text="dialog.reset"
      />
    </v-col>
    <main-loader v-else />
  </v-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import SidebarForm from '@/components/SidebarForm';
import MainLoader from '@/components/MainLoader';
import ProfilesWidget from '@/components/ProfilesWidget';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import { get, call } from 'vuex-pathify';

export default {
  name: 'WaterBalanceWizard',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    SidebarRow,
    SidebarForm,
    MainLoader,
    ProfilesWidget,
    Stepper: () => import('@/components/Stepper'),
    DataInput: () => import('@/components/DataInput'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    withProfiles: {
      type: Boolean,
      default: false,
    },
    useAnalysisDate: {
      type: Boolean,
      default: false,
    },
    toolName: {
      type: String,
      required: true,
    },
    inputs: {
      type: Array,
      default: () => [],
    },
    steps: {
      type: Array,
      default: () => [],
    },
    paramsProxy: {
      type: Object,
      default: () => ({}),
    },
    stepsValidations: {
      type: Object,
      default: () => ({}),
    },
    profilesData: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    isAnalysisLoading: false,
    isAnalysisDone: false,
    isFetched: false,
    currentProfile: null,
    itemsList: {},
    inputLoading: {},
    isResetDialogVisible: false,
    layerName: '',
    currentTaskId: null,
  }),
  methods: {
    generateWaterBalanceStatistics: call('waterBalance/generateWaterBalanceStatistics'),
    generateWaterBalanceDma: call('waterBalance/generateWaterBalanceDma'),
    checkAnalysisStatus: call('celeryTasks/getCeleryTaskStatus'),
    getResult: call('celeryTasks/getCeleryTaskResult'),
    cancelAnalysis: call('celeryTasks/stopCeleryTask'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    getLayers: call('layers/getLayers'),
    getCurrentUser: call('users/getCurrentUser'),
    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),
    async turnOnAnalysis() {
      this.isAnalysisDone = false;
      this.isAnalysisLoading = true;
      try {
        const payload = this.getPayload();
        const taskId =
          this.toolName === 'dma'
            ? await this.generateWaterBalanceDma(payload)
            : await this.generateWaterBalanceStatistics({ name: this.toolName, payload });
        this.currentTaskId = taskId;
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            const status = await this.checkAnalysisStatus(taskId);
            if (['REVOKED', 'FAILURE'].includes(status)) {
              this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
              this.$store.set('snackbar/PUSH_ERROR!', {
                message: this.$i18n.t('errors.backend.requestError'),
                timeout: -1,
              });
              this.isAnalysisLoading = false;
              this.currentTaskId = null;
              return 'cancel';
            }
            return status === 'SUCCESS';
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            const r = await this.getResult({ id: taskId, type: 'data' });
            await this.getLayers();
            const layer = JSON.parse(JSON.stringify(this.layers[r.result.layer]));
            if (layer) {
              await this.getLayerMetadata({
                dataSource: layer.data_source_name,
                params: { with_attributes_schema: true },
              });
              await this.getCurrentUser();
              layer.labels_visible = layer.labels_visible || false;
              layer.style = layer.style_web;
              delete layer.style_web;
              this.$store.set('layers/ADD_PROJECT_LAYERS!', [
                { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
              ]);
              this.$root.$emit('pushProjectLayers', [
                { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
              ]);
            }
            this.layerName = this.layers[r.result.layer].name;
            this.currentTaskId = null;
            this.isAnalysisDone = true;
            this.isAnalysisLoading = false;
          },
          interval: 5000,
        });
      } catch (error) {
        this.isAnalysisLoading = false;
        this.currentTaskId = null;
        console.log('error', error);
      }
    },
    resetAnalysis() {
      this.isResetDialogVisible = false;
      const layerValues = {};
      this.inputs.forEach(input => {
        if (input.isLayer) layerValues[input.name] = this.params[input.name];
      });
      Object.keys(this.stepsValidations).forEach(step => {
        this.$refs[step][0]?.reset();
      });
      this.$refs[`waterBalanceStepper-${this.toolName}`]?.reset();
      this.$nextTick(() => {
        Object.entries(layerValues).forEach(([key, value]) => {
          this.params[key] = value;
        });
      });
    },
    inputChangeHandler(data, step) {
      if (data.isLayer) {
        const layer = this.params[data.name];
        this.$refs[`step${step}`][0]?.reset();
        this.params[data.name] = layer;
      }
      if (data.connectedValue) {
        Array.isArray(data.connectedValue)
          ? data.connectedValue.forEach(value => (this.params[value] = null))
          : (this.params[data.connectedValue] = null);
      }
    },
    getPayload() {
      let paramsCopy = JSON.parse(JSON.stringify(this.params));
      return Object.fromEntries(
        Object.entries(paramsCopy).filter(([key]) => !this.inputs.find(input => input.name === key)?.skip)
      );
    },
    async handleAttributeChange(nV, input) {
      const currentLayer = this.layers[this.params[input.connectedLayer]];
      if (currentLayer && nV) {
        this.inputLoading[input.name] = true;
        try {
          const uniqueValues = await this.getDataSourceAttributeUniqueValues({
            dataSourceName: currentLayer.data_source_name,
            attributeName: nV,
            params: {
              features_filter: currentLayer.filter_expression,
            },
          });
          this.itemsList[input.name] = Object.freeze(uniqueValues);
        } catch (e) {
          console.log('error', e);
        } finally {
          this.inputLoading[input.name] = false;
          this.$forceUpdate();
        }
      }
    },
    handleLayerChange(nV, inputName, step) {
      if (!this.projectLayers.find(x => x.layer_id === this.params[inputName])) {
        this.$nextTick(() => {
          this.$refs[`step${step}`][0]?.reset();
        });
      }
      const currentLayer = this.layers[nV];
      if (currentLayer) {
        const dataSourceAttributes = this.dataSources[currentLayer?.data_source_name]?.attributes_schema.attributes;
        const schemaForm = this.getForm(currentLayer?.form_schema.elements, dataSourceAttributes);
        this.itemsList[inputName] = this.$_flattenStructure(schemaForm, 'elements');
      } else {
        this.itemsList[inputName] = [];
      }
    },
    attachListeners() {
      this.inputs.forEach(input => {
        if (input.isLayer) {
          this.$watch(`params.${input.name}`, nV => {
            if (nV) this.handleLayerChange(nV, input.name, input.step);
          });
        }
        if (input.connectedValue && !input.isNotAttribute) {
          this.$watch(`params.${input.name}`, nV => {
            if (nV) this.handleAttributeChange(nV, input);
          });
        }
      });
    },
    init() {
      this.attachListeners();
      this.isFetched = true;
    },
    async changeCurrentProfile(newProfile) {
      if (newProfile) {
        let currentProfileCopy = JSON.parse(JSON.stringify(newProfile));
        delete currentProfileCopy.id;
        delete currentProfileCopy.default;
        delete currentProfileCopy.name;
        await this.$nextTick();
        this.params = currentProfileCopy;
      }
    },
  },
  computed: {
    projectElements: get('layers/project@layers'),
    dataSources: get('layers/metadata'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    layers: get('layers/layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    params: {
      get() {
        return this.paramsProxy;
      },
      set(nV) {
        this.$emit('update:paramsProxy', nV);
      },
    },
    allDataValid() {
      return (
        Object.values(this.stepsValidations).every(step => !!step) &&
        (this.useAnalysisDate ? !!this.params['analysis_date'] : true)
      );
    },
  },
  beforeDestroy() {
    if (this.currentTaskId) {
      this.cancelAnalysis(this.currentTaskId);
    }
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
::v-deep .wizard-form .row {
  padding-right: 0 !important;
}
::v-deep {
  .v-select__selections {
    flex-wrap: wrap !important;
  }
  .v-alert--text:before {
    opacity: 0;
  }
}
</style>
